'use client';

import { useEffect, useRef } from 'react';

import { Spinner } from './spinner.component';

import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';

interface InfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  dataAnalyticsEvent?: AnalyticsEnumKeyType;
}

export const InfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
  dataAnalyticsEvent,
}: InfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin },
    );

    const { current } = sentinelRef;

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [hasMore, isLoading, onLoadMore, rootMargin]);

  if (!hasMore && !isLoading) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center py-5 w-full text-green-500">
      {isLoading && <Spinner />}

      <>
        {dataAnalyticsEvent && <TrackUserView eventName={dataAnalyticsEvent} />}

        <div ref={sentinelRef} className="h-1 w-full" />
      </>
    </div>
  );
};
